import { TagSeverity } from '@/shared/enums/generic.enum';
import { DealQuestionType, GeneralQuestionCategory, QuestionRole, QuestionScope, QuestionStatus } from './questions.enum';
import { DealQuestionDto, GeneralQuestionDto, QuestionListItem, QuestionReply, QuestionReplyDto } from './questions.model';
import { dealQuestionTypeOptions, generalQuestionCategoryOptions, questionRoleOptions, questionStatusOptions } from './questions.option';

function mapReplies(replies: QuestionReplyDto[] | null | undefined): QuestionReply[] {
    return (replies ?? [])
        .map((reply) => ({
            id: reply.id,
            createdByUserId: reply.createdByUserId,
            createdByUserName: reply.createdByUserName?.trim() || '-',
            createdByRole: reply.createdByRole,
            message: reply.message ?? '',
            createdAt: reply.createdAt
        }))
        .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
}

export function mapDealQuestionToListItem(dto: DealQuestionDto): QuestionListItem {
    const participants = [dto.clientName, dto.relationshipManagerName, dto.wealthManagerUserName, dto.wealthManagerEntityName].map((name) => name?.trim()).filter((name): name is string => !!name);

    return {
        scope: QuestionScope.DEAL,
        id: dto.id,
        dealOpportunityId: dto.dealOpportunityId ?? null,
        dealName: dto.dealName?.trim() || '',
        title: dto.question ?? '',
        body: dto.question ?? '',
        topicValue: dto.type,
        topicLabelKey: getDealQuestionTypeLabelKey(dto.type),
        status: dto.status,
        createdByUserId: dto.createdByUserId,
        createdByUserName: dto.createdByUserName?.trim() || '-',
        createdByRole: dto.createdByRole,
        canAnswer: dto.canAnswer === true,
        canAcceptAnswer: dto.canAcceptAnswer === true,
        replies: mapReplies(dto.replies),
        lastActivityAt: dto.lastReplyAt ?? dto.updatedAt ?? dto.createdAt ?? null,
        resolvedAt: dto.resolvedAt ?? null,
        createdAt: dto.createdAt,
        participants: [...new Set(participants)].join(' · ')
    };
}

export function mapGeneralQuestionToListItem(dto: GeneralQuestionDto): QuestionListItem {
    return {
        scope: QuestionScope.GENERAL,
        id: dto.id,
        dealOpportunityId: null,
        dealName: '',
        title: dto.subject ?? '',
        body: dto.details ?? '',
        topicValue: dto.category,
        topicLabelKey: getGeneralQuestionCategoryLabelKey(dto.category),
        status: dto.status,
        createdByUserId: dto.createdByUserId,
        createdByUserName: dto.createdByUserName?.trim() || '-',
        createdByRole: dto.createdByRole,
        canAnswer: dto.canAnswer === true,
        canAcceptAnswer: dto.canAcceptAnswer === true,
        replies: mapReplies(dto.replies),
        lastActivityAt: dto.lastReplyAt ?? dto.updatedAt ?? dto.createdAt ?? null,
        resolvedAt: dto.resolvedAt ?? null,
        createdAt: dto.createdAt,
        participants: ''
    };
}

export function getDealQuestionTypeLabelKey(type: DealQuestionType | number | null | undefined): string {
    return dealQuestionTypeOptions.find((option) => option.value === type)?.labelKey ?? 'QUESTIONS.TYPE.OTHER';
}

export function getGeneralQuestionCategoryLabelKey(category: GeneralQuestionCategory | number | null | undefined): string {
    return generalQuestionCategoryOptions.find((option) => option.value === category)?.labelKey ?? 'QUESTIONS.CATEGORY.OTHER';
}

export function getQuestionStatusLabelKey(status: QuestionStatus | number | null | undefined): string {
    return questionStatusOptions.find((option) => option.value === status)?.labelKey ?? 'QUESTIONS.STATUS.OPEN';
}

export function getQuestionRoleLabelKey(role: QuestionRole | number | null | undefined): string {
    return questionRoleOptions.find((option) => option.value === role)?.labelKey ?? 'QUESTIONS.ROLE.ADMIN';
}

export function getQuestionDealLabel(item: QuestionListItem): string {
    if (item.dealName) return item.dealName;
    return item.dealOpportunityId != null ? `#${item.dealOpportunityId}` : '-';
}

export function getQuestionStatusSeverity(status: QuestionStatus | number | null | undefined): TagSeverity {
    switch (status) {
        case QuestionStatus.ANSWERED:
            return TagSeverity.INFO;
        case QuestionStatus.CLOSED:
            return TagSeverity.SUCCESS;
        default:
            return TagSeverity.WARNING;
    }
}
